/** @jsxImportSource @emotion/react */
import React from "react";
import { Alert, Col, Form, Input, Modal, Row, Button } from "antd";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import * as Styles from "./styles";
import { AuthUsecase } from "../../../usecases/Auth/usecase";
import { useAuthDispatch, useAuthState } from "../../../usecases/Auth";
import { SignedInDto } from "../../../services/Auth/dto/signedIn.dto";
import { TenantUserUsecase } from "../../../usecases/TenantUser";

interface IFormInputs {
  lastName: string;
  firstName: string;
}

const formSchema = yup.object().shape({
  lastName: yup.string().required("Please enter last name"),
  firstName: yup.string().required("Please enter first name"),
});

interface ModalEditProfileProps {
  onCancel: () => void;
  onSuccess: () => void;
  visible: boolean;
  item: SignedInDto;
}

const ModalEditProfileComponent: React.FC<ModalEditProfileProps> = ({
  visible,
  onCancel,
  onSuccess,
  item,
}: ModalEditProfileProps) => {
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm<IFormInputs>({
    mode: "onChange",
    defaultValues: {
      firstName: item.attributes.given_name,
      lastName: item.attributes.family_name,
    } as IFormInputs,
    resolver: yupResolver(formSchema),
  });
  const authState = useAuthState();
  const authDispatch = useAuthDispatch();

  const handleSubmitHandle = React.useCallback(
    async (data: IFormInputs) => {
      setIsProcessing(true);
      try {
        const authUsecase = new AuthUsecase(authDispatch);
        const tenantUserUsecase = new TenantUserUsecase();
        await tenantUserUsecase.update({
          id: authState.userInfo?.username || "",
          userFirstName: data.firstName,
          userLastName: data.lastName,
        });
        await authUsecase.syncUserInfo(true);

        reset();
        setErrorMessage("");
        onSuccess();
      } catch (error) {
        console.error(error);
        setErrorMessage(error.message);
      } finally {
        setIsProcessing(false);
      }
    },
    [authDispatch, reset, onSuccess, authState.userInfo],
  );

  return (
    <Modal
      title="Edit Full Name"
      visible={visible}
      closable={false}
      footer={[
        <Button key="back" size="large" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          size="large"
          loading={isProcessing}
          onClick={handleSubmit(handleSubmitHandle)}
        >
          Save
        </Button>,
      ]}
    >
      <Form layout="vertical" onFinish={handleSubmit(handleSubmitHandle)}>
        {errorMessage && (
          <Row justify="center">
            <Col css={Styles.colGutterStyle}>
              <Alert message={errorMessage} type="error" />
            </Col>
          </Row>
        )}
        <Form.Item
          label="First Name"
          css={Styles.labelStyle}
          validateStatus={errors.firstName ? "error" : "validating"}
          help={errors.firstName?.message}
        >
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => <Input {...field} size="large" />}
          />
        </Form.Item>
        <Form.Item
          label="Last Name"
          css={Styles.labelStyle}
          validateStatus={errors.lastName ? "error" : "validating"}
          help={errors.lastName?.message}
        >
          <Controller
            name="lastName"
            control={control}
            render={({ field }) => <Input {...field} size="large" />}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export const ModalEditProfile = React.memo(ModalEditProfileComponent);
